import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Zap, Phone, UserPlus, Calendar, FileText, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickActionWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [form, setForm] = useState({});

  // Only show in CRM/admin pages
  if (!location.pathname.startsWith("/admin")) return null;

  const actions = [
    { icon: Phone, label: "Log a Call", type: "call", color: "bg-green-500 hover:bg-green-600" },
    { icon: UserPlus, label: "Add Lead", type: "lead", color: "bg-blue-500 hover:bg-blue-600" },
    { icon: Calendar, label: "Schedule", type: "schedule", color: "bg-purple-500 hover:bg-purple-600" },
    { icon: FileText, label: "Quick Note", type: "note", color: "bg-orange-500 hover:bg-orange-600" },
  ];

  const openAction = (type) => {
    setDialogType(type);
    setForm({});
    setOpen(false);
  };

  const handleSave = async () => {
    try {
      if (dialogType === "lead") {
        if (!form.name) { toast.error("Name required"); return; }
        await axios.post(`${API}/crm/clients`, { name: form.name, email: form.email || "", phone: form.phone || "", source: form.source || "manual", pipeline_stage: "new_lead" }, { withCredentials: true });
        toast.success("Lead added!");
      } else if (dialogType === "call") {
        if (!form.client_name) { toast.error("Client name required"); return; }
        // Create as interaction note
        await axios.post(`${API}/crm/clients`, { name: form.client_name, phone: form.phone || "", source: "cold_call", notes: `Call log: ${form.notes || ""}`, pipeline_stage: "contacted" }, { withCredentials: true });
        toast.success("Call logged!");
      } else if (dialogType === "schedule") {
        if (!form.title || !form.date) { toast.error("Title and date required"); return; }
        // Need a client - create one if doesn't exist
        let clientId = form.client_id;
        if (!clientId && form.client_name) {
          const { data } = await axios.post(`${API}/crm/clients`, { name: form.client_name, source: "manual", pipeline_stage: "contacted" }, { withCredentials: true });
          clientId = data.id;
        }
        if (!clientId) { toast.error("Client required"); return; }
        await axios.post(`${API}/crm/appointments`, { client_id: clientId, title: form.title, date: form.date, time: form.time || "09:00", notes: form.notes || "" }, { withCredentials: true });
        toast.success("Appointment scheduled!");
      } else if (dialogType === "note") {
        if (!form.client_name || !form.notes) { toast.error("Client and note required"); return; }
        await axios.post(`${API}/crm/clients`, { name: form.client_name, notes: form.notes, source: "manual", pipeline_stage: "new_lead" }, { withCredentials: true });
        toast.success("Note saved!");
      }
      setDialogType(null);
    } catch { toast.error("Error saving"); }
  };

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-6 right-6 z-40">
        {open && (
          <div className="absolute bottom-16 right-0 flex flex-col gap-2 items-end mb-2">
            {actions.map((a) => (
              <button
                key={a.type}
                onClick={() => openAction(a.type)}
                className={`${a.color} text-white px-4 py-2 rounded-sm text-sm font-medium flex items-center gap-2 shadow-lg transition-colors`}
              >
                <a.icon className="w-4 h-4" /> {a.label}
              </button>
            ))}
          </div>
        )}
        <button
          data-testid="quick-action-fab"
          onClick={() => setOpen(!open)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${open ? "bg-gray-800 rotate-45" : "bg-yellow-400 hover:bg-yellow-500"}`}
        >
          {open ? <X className="w-6 h-6 text-white" /> : <Zap className="w-6 h-6 text-gray-900" />}
        </button>
      </div>

      {/* Quick dialogs */}
      <Dialog open={!!dialogType} onOpenChange={(o) => !o && setDialogType(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {dialogType === "call" && "Log a Call"}
              {dialogType === "lead" && "Add Lead"}
              {dialogType === "schedule" && "Schedule Follow-up"}
              {dialogType === "note" && "Quick Note"}
            </DialogTitle>
            <DialogDescription>Quick entry — fill what you have.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            {dialogType === "lead" && (
              <>
                <Input placeholder="Name *" value={form.name || ""} onChange={e => handleChange("name", e.target.value)} />
                <Input placeholder="Phone" value={form.phone || ""} onChange={e => handleChange("phone", e.target.value)} />
                <Input placeholder="Email" value={form.email || ""} onChange={e => handleChange("email", e.target.value)} />
                <Select value={form.source || "manual"} onValueChange={v => handleChange("source", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="cold_call">Cold Call</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {dialogType === "call" && (
              <>
                <Input placeholder="Client name *" value={form.client_name || ""} onChange={e => handleChange("client_name", e.target.value)} />
                <Input placeholder="Phone number" value={form.phone || ""} onChange={e => handleChange("phone", e.target.value)} />
                <Textarea placeholder="Call notes..." value={form.notes || ""} onChange={e => handleChange("notes", e.target.value)} />
              </>
            )}
            {dialogType === "schedule" && (
              <>
                <Input placeholder="Client name *" value={form.client_name || ""} onChange={e => handleChange("client_name", e.target.value)} />
                <Input placeholder="Title *" value={form.title || ""} onChange={e => handleChange("title", e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={form.date || ""} onChange={e => handleChange("date", e.target.value)} />
                  <Input type="time" value={form.time || "09:00"} onChange={e => handleChange("time", e.target.value)} />
                </div>
                <Textarea placeholder="Notes..." value={form.notes || ""} onChange={e => handleChange("notes", e.target.value)} />
              </>
            )}
            {dialogType === "note" && (
              <>
                <Input placeholder="Client name *" value={form.client_name || ""} onChange={e => handleChange("client_name", e.target.value)} />
                <Textarea placeholder="Your note... *" value={form.notes || ""} onChange={e => handleChange("notes", e.target.value)} rows={4} />
              </>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setDialogType(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm border border-gray-200">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
